import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '../utils/toast';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddEditUserModal = ({ isOpen, onClose, onSuccess, editUser = null, officers = [] }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    password: '',
    type: '', // CRM Officer or CRM Associate
    assigned_officer_id: ''
  });

  useEffect(() => {
    if (editUser) {
      // Populate form for editing
      setFormData({
        full_name: editUser.full_name || '',
        email: editUser.email || '',
        phone_number: editUser.phone_number || '',
        password: '', // Don't populate password for security
        type: editUser.role || '',
        assigned_officer_id: editUser.reporting_to || ''
      });
    } else {
      // Reset form for adding
      setFormData({
        full_name: '',
        email: '',
        phone_number: '',
        password: '',
        type: '',
        assigned_officer_id: ''
      });
    }
  }, [editUser, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      type: value,
      assigned_officer_id: value === 'CRM Officer' ? '' : prev.assigned_officer_id
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.full_name.trim()) {
      toast.error('Please enter full name');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Please enter email');
      return;
    }
    if (!formData.phone_number.trim()) {
      toast.error('Please enter phone number');
      return;
    }
    if (!editUser && !formData.password.trim()) {
      toast.error('Please enter password');
      return;
    }
    if (!formData.type) {
      toast.error('Please select user type');
      return;
    }
    if (formData.type === 'CRM Associate' && !formData.assigned_officer_id) {
      toast.error('Please assign an officer for the associate');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare payload
      const payload = {
        full_name: formData.full_name,
        email: formData.email,
        phone_number: formData.phone_number,
        role: formData.type
      };

      // Add password only if provided
      if (formData.password.trim()) {
        payload.password = formData.password;
      }

      // Add reporting_to for CRM Associate
      if (formData.type === 'CRM Associate') {
        payload.reporting_to = formData.assigned_officer_id;
      }

      if (editUser) {
        // Update existing user
        await axios.put(
          `${API}/crm/users/${editUser.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('User updated successfully');
      } else {
        // Create new user
        await axios.post(
          `${API}/crm/users`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('User added successfully');
      }

      onSuccess();
      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
        (editUser ? 'Failed to update user' : 'Failed to add user');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editUser ? 'Edit User' : 'Add User'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="full_name">Full Name <span className="text-red-500">*</span></Label>
            <Input
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleInputChange}
              placeholder="Enter full name"
            />
          </div>

          {/* Type */}
          <div className="space-y-2">
            <Label htmlFor="type">Type <span className="text-red-500">*</span></Label>
            <Select value={formData.type} onValueChange={handleTypeChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select user type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CRM Officer">CRM Officer</SelectItem>
                <SelectItem value="CRM Associate">CRM Associate</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reporting CRM Officer (only for CRM Associate) */}
          {formData.type === 'CRM Associate' && (
            <div className="space-y-2">
              <Label htmlFor="assigned_officer_id">Reporting CRM Officer <span className="text-red-500">*</span></Label>
              <Select value={formData.assigned_officer_id} onValueChange={(value) => setFormData(prev => ({ ...prev, assigned_officer_id: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reporting CRM officer" />
                </SelectTrigger>
                <SelectContent>
                  {officers.map(officer => (
                    <SelectItem key={officer.id} value={officer.id}>
                      {officer.full_name} ({officer.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email"
              disabled={!!editUser} // Email cannot be changed when editing
            />
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <Label htmlFor="phone_number">Phone Number <span className="text-red-500">*</span></Label>
            <Input
              id="phone_number"
              name="phone_number"
              type="tel"
              value={formData.phone_number}
              onChange={handleInputChange}
              placeholder="Enter phone number"
            />
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">
              Password {!editUser && <span className="text-red-500">*</span>}
              {editUser && <span className="text-gray-500 text-xs"> (leave blank to keep current)</span>}
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={editUser ? "Enter new password" : "Enter password"}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {editUser ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                editUser ? 'Update User' : 'Add User'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditUserModal;
